/**
 * VerifyMagicLink.js
 * Handles magic link verification and automatic login
 * Displays loading state while verifying token
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import './VerifyMagicLink.css';

const DEFAULT_REDIRECT = '/my-knowledge-entries';

/**
 * Validate a redirect path to prevent open redirect attacks.
 * Only allows relative paths starting with a single slash.
 * Rejects absolute URLs, protocol-relative URLs, backslash tricks,
 * javascript: URIs, and authority components.
 */
function getSafeRedirect(rawRedirect) {
  if (!rawRedirect || typeof rawRedirect !== 'string') {
    return DEFAULT_REDIRECT;
  }

  const trimmed = rawRedirect.trim();

  // Must start with exactly one forward slash (not // or /\)
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.startsWith('/\\')) {
    return DEFAULT_REDIRECT;
  }

  // Block protocol schemes (javascript:, data:, vbscript:, etc.)
  if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:/i.test(trimmed)) {
    return DEFAULT_REDIRECT;
  }

  // Block backslashes anywhere (browser normalization can turn /\ into //)
  if (trimmed.includes('\\')) {
    return DEFAULT_REDIRECT;
  }

  // Block authority component (@) which could be used in URL tricks
  if (trimmed.includes('@')) {
    return DEFAULT_REDIRECT;
  }

  return trimmed;
}

const VerifyMagicLink = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserFromMagicLink } = useAuth();

  // Extract redirect value as a primitive string outside the effect.
  // This avoids adding searchParams (an object reference) to the dependency
  // array, which could cause re-render loops depending on the router version.
  const redirectParam = searchParams.get('redirect');

  const [status, setStatus] = useState({
    type: 'loading', // 'loading' | 'success' | 'error'
    message: 'Verifying magic link...'
  });

  // Ref guard to prevent duplicate verification calls in StrictMode
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    const verifyToken = async () => {
      // Prevent duplicate API calls (React StrictMode runs effects twice in dev)
      if (hasVerifiedRef.current) {
        console.log('Verification already initiated, skipping duplicate call');
        return;
      }

      // Mark as verified before making the API call
      hasVerifiedRef.current = true;

      if (!token) {
        setStatus({
          type: 'error',
          message: 'Invalid magic link'
        });
        return;
      }

      try {
        console.log('Verifying magic link token...');

        const response = await authService.verifyMagicLink(token);

        if (response.success) {
          console.log('Magic link verified successfully');

          // Update auth context with user data
          if (setUserFromMagicLink) {
            setUserFromMagicLink(response.user, response.sidebarCounts);
          }

          setStatus({
            type: 'success',
            message: 'Sign in successful! Redirecting...'
          });

          // Redirect after 1.5 seconds (welcome links redirect to /account)
          const redirectPath = getSafeRedirect(redirectParam);
          setTimeout(() => {
            navigate(redirectPath);
          }, 1500);
        } else {
          setStatus({
            type: 'error',
            message: response.message || 'Magic link verification failed'
          });
        }

      } catch (error) {
        console.error('Magic link verification error:', error);

        setStatus({
          type: 'error',
          message: error.response?.data?.message ||
            'This magic link is invalid or has expired. Please request a new one.'
        });
      }
    };

    verifyToken();
  }, [token, navigate, setUserFromMagicLink, redirectParam]);

  return (
    <div className="verify-magic-link-container">
      <div className="verify-magic-link-card">
        {status.type === 'loading' && (
          <div className="verify-status loading">
            <div className="spinner"></div>
            <h2>Verifying Magic Link</h2>
            <p>Please wait while we sign you in...</p>
          </div>
        )}

        {status.type === 'success' && (
          <div className="verify-status success">
            <div className="success-icon">✓</div>
            <h2>Sign In Successful!</h2>
            <p>Redirecting you to your knowledge base...</p>
          </div>
        )}

        {status.type === 'error' && (
          <div className="verify-status error">
            <div className="error-icon">✕</div>
            <h2>Magic Link Invalid</h2>
            <p className="error-message">{status.message}</p>

            <div className="error-actions">
              <Link to="/forgot-password" className="button primary">
                Request New Magic Link
              </Link>
              <Link to="/login" className="button secondary">
                Back to Login
              </Link>
            </div>

            <div className="error-help">
              <p className="help-text">
                <strong>Common reasons:</strong>
              </p>
              <ul>
                <li>The link has expired (links expire after 15 minutes)</li>
                <li>The link has already been used</li>
                <li>The link was copied incorrectly</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyMagicLink;
